import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';

interface SubscriptionGuardProps {
    children: React.ReactNode;
}

/**
 * SubscriptionGuard
 * Ensures that agencies have a subscription (including trial).
 * Only redirects to pricing if:
 * - User is approved AND
 * - Has NO subscription at all (null, not even trialing)
 * 
 * Allows: trial (trialing), active, expired (can still view/extend)
 * Blocks: null or pending_payment only
 */
const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children }) => {
    const location = useLocation();
    const { user, userType, isLoading: authLoading } = useAuthStore();
    const { subscription, fetchSubscription, isLoading, hasFetchedOnce } = useSubscriptionStore();
    const isInitialSubscriptionLoad = !hasFetchedOnce && isLoading;

    // Load subscription on mount so guard decisions use fresh backend state
    useEffect(() => {
        if (userType === 'employee') return;
        // Avoid fetching on pricing/subscription routes
        const path = location.pathname;
        if (path.startsWith('/pricing') || path.startsWith('/subscription')) return;

        fetchSubscription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty deps - fetch only once on component mount

    // Employee page authorization is independent of the owner's billing panel.
    if (userType === 'employee') return <>{children}</>;

    // Show loading if auth or subscription is still loading, OR if subscription hasn't been fetched yet
    if (authLoading || isInitialSubscriptionLoad || !hasFetchedOnce) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Redirect to pricing ONLY if:
    // - User is an agency owner (not an employee) AND
    // - User is approved AND
    // - NO subscription exists at all (null) AND
    // - Subscription fetch has completed (hasFetchedOnce is true)
    const shouldRedirectToPricing =
        userType === 'agency' &&
        user?.status === 'approved' &&
        subscription === null &&
        hasFetchedOnce &&
        !location.pathname.startsWith('/pricing');

    if (shouldRedirectToPricing) {
        return <Navigate to="/pricing" replace />;
    }

    return <>{children}</>;
};

export default SubscriptionGuard;
